import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  fetchOfferteDetail,
  resendOfferte,
  setOfferteStatus,
  type OfferteDetail,
  type OfferteProduct,
  type OffertePackage,
  type OfferteDiscount,
  type OfferteServiceAddress,
} from '../../lib/offertes';
import { LEAD_CLOSED_STATUS, type PackageSku } from '../../lib/offerte-constants';
import { OfferteStatusSelect } from './OfferteStatusSelect';
import { TerugkomMomentPill } from './TerugkomMomentPill';
import { CallButton } from '../phone/CallButton';
import type { Brand } from '../../lib/telephony';
import { BRAND_COLORS } from '../../lib/order-constants';
import { formatCurrency, formatDate, formatDateTime } from '../../lib/format';
import { removeKleedBehandeling } from '../../lib/kleed-behandeling';
import { removeLeadProduct } from '../../lib/lead-product';
import { downloadOffertePdf } from '../../lib/offerte-pdf';
import { useOffertePanelStore } from '../../stores/offerte-panel-store';
import { ConfirmDialog } from '../offerte-panel/ConfirmDialog';
import { OutboundComposerModal } from '../outbound/OutboundComposerModal';
import { KleedBehandelingDialog } from './KleedBehandelingDialog';
import { EditCustomerDialog } from './EditCustomerDialog';
import { ServiceAddressDialog } from './ServiceAddressDialog';
import { EditKleedDialog } from './EditKleedDialog';
import { AddKleedDialog } from './AddKleedDialog';
import { AddOfferteFeeDialog } from './AddOfferteFeeDialog';
import { SendPdfDialog } from './SendPdfDialog';
import { VerzondenOffertesSection } from './VerzondenOffertesSection';
import { OfferteKortingPill } from './OfferteKortingPill';
import { BundelkortingToggle } from '../common/BundelkortingToggle';

type Variant = 'page' | 'drawer';

interface OfferteDetailViewProps {
  offerteId: string;
  variant: Variant;
  onClose?: () => void;
}

function ExternalLinkIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
    </svg>
  );
}

function isBehandeling(p: OfferteProduct): boolean {
  return p.itemType === 'behandeling' || !!p.parentLineItemId;
}

function isToeslag(p: OfferteProduct): boolean {
  return p.itemType === 'toeslag';
}

function groupKledenMetBehandelingen(products: OfferteProduct[]): Array<{
  kleed: OfferteProduct;
  behandelingen: OfferteProduct[];
}> {
  const kleden = products.filter((p) => !isBehandeling(p) && !isToeslag(p));
  const behandelingen = products.filter(isBehandeling);
  const byParent = new Map<string, OfferteProduct[]>();
  for (const b of behandelingen) {
    const key = b.parentLineItemId ?? '';
    if (!key) continue;
    const arr = byParent.get(key) ?? [];
    arr.push(b);
    byParent.set(key, arr);
  }
  return kleden.map((kleed) => ({
    kleed,
    behandelingen: byParent.get(kleed.id) ?? [],
  }));
}

interface KleedCardProps {
  kleed: OfferteProduct;
  behandelingen: OfferteProduct[];
  onAddBehandeling: (parentId: string, parentName: string) => void;
  onRemoveBehandeling: (childId: string) => void;
  onEditKleed: (kleed: OfferteProduct) => void;
  onRemoveKleed: (kleed: OfferteProduct) => void;
  removingId: string | null;
  removingKleedId: string | null;
  mutationsAllowed: boolean;
}

function KleedCard({
  kleed,
  behandelingen,
  onAddBehandeling,
  onRemoveBehandeling,
  onEditKleed,
  onRemoveKleed,
  removingId,
  removingKleedId,
  mutationsAllowed,
}: KleedCardProps) {
  const dimensions = kleed.diameterCm
    ? `Ø ${kleed.diameterCm} cm`
    : kleed.lengthCm && kleed.widthCm
      ? `${kleed.lengthCm}×${kleed.widthCm} cm`
      : '';
  // Bruto incl. (vol tarief, vóór korting) per regel — zelfde weergave als de PDF.
  // Een eventuele order-korting wordt onderaan als aparte regel verrekend.
  const kleedTotal = kleed.totaalInclBtwBruto ?? kleed.totaalInclBtw ?? 0;
  const behandelingenTotal = behandelingen.reduce(
    (sum, b) => sum + (b.totaalInclBtwBruto ?? b.totaalInclBtw ?? 0),
    0,
  );
  const combinedTotal = kleedTotal + behandelingenTotal;
  const label = kleed.productName || kleed.productCode || 'Vloerkleed';

  return (
    <div className="px-6 py-3 space-y-2">
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{label}</div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            {dimensions && <span>{dimensions}</span>}
            {kleed.oppervlakteM2 > 0 && <span>{kleed.oppervlakteM2.toFixed(2)} m²</span>}
            {kleed.quantity > 1 && <span>×{kleed.quantity}</span>}
          </div>
          {kleed.externeReferentie && (
            <div className="text-xs text-slate-500 mt-1">
              <span className="font-medium text-slate-600">Ref:</span> {kleed.externeReferentie}
            </div>
          )}
          {kleed.bijzonderheden && (
            <div className="text-xs text-slate-500 mt-1 italic">{kleed.bijzonderheden}</div>
          )}
        </div>
        <div className="text-sm font-medium text-slate-900 flex-shrink-0">
          {formatCurrency(kleedTotal)}
        </div>
        {mutationsAllowed && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => onEditKleed(kleed)}
              className="text-slate-300 hover:text-indigo-600"
              aria-label="Maten of bijzonderheden bewerken"
              title="Maten/bijzonderheden bewerken"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
            </button>
            <button
              type="button"
              disabled={removingKleedId === kleed.id}
              onClick={() => onRemoveKleed(kleed)}
              className="text-slate-300 hover:text-red-600 disabled:text-slate-200"
              aria-label="Kleed verwijderen"
              title="Kleed verwijderen"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {behandelingen.length > 0 && (
        <ul className="ml-4 border-l-2 border-slate-100 pl-3 space-y-1">
          {behandelingen.map((b) => (
            <li key={b.id} className="flex items-center gap-3 text-xs text-slate-600">
              <svg
                className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                />
              </svg>
              <span className="flex-1 truncate">{b.productName || b.productCode}</span>
              <span className="flex-shrink-0 text-slate-500">
                +{formatCurrency(b.totaalInclBtwBruto ?? b.totaalInclBtw ?? 0)}
              </span>
              <button
                type="button"
                disabled={removingId === b.id}
                onClick={() => onRemoveBehandeling(b.id)}
                className="text-slate-300 hover:text-red-600 disabled:text-slate-200 flex-shrink-0"
                aria-label="Behandeling verwijderen"
                title="Behandeling verwijderen"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between ml-4 pl-3 pt-1">
        {mutationsAllowed ? (
          <button
            type="button"
            onClick={() => onAddBehandeling(kleed.id, label)}
            className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Behandeling toevoegen
          </button>
        ) : (
          <span />
        )}
        {behandelingen.length > 0 && (
          <div className="text-xs text-slate-500">
            {formatCurrency(kleedTotal)} + {formatCurrency(behandelingenTotal)} ={' '}
            <span className="font-medium text-slate-900">{formatCurrency(combinedTotal)}</span>{' '}
            incl. btw
          </div>
        )}
      </div>
    </div>
  );
}

function PackageCard({
  pkg,
  isCurrent,
  onChoose,
}: {
  pkg: OffertePackage;
  isCurrent: boolean;
  onChoose: () => void;
}) {
  return (
    <div
      className={clsx(
        'relative bg-white rounded-xl border p-4 flex flex-col gap-3',
        pkg.isPopular ? 'border-indigo-400 shadow-md' : 'border-slate-200 shadow-sm'
      )}
    >
      {pkg.isPopular && (
        <span className="absolute -top-2 right-4 text-[10px] px-2 py-0.5 rounded-full bg-indigo-600 text-white font-medium">
          Populair
        </span>
      )}
      <div>
        <div className="text-sm font-semibold text-slate-900">{pkg.label}</div>
        {pkg.description && (
          <p className="text-xs text-slate-500 mt-0.5">{pkg.description}</p>
        )}
      </div>
      <div>
        <div className="text-2xl font-semibold text-slate-900">
          {formatCurrency(pkg.priceInclBtw)}
        </div>
        {pkg.priceDifference > 0 ? (
          <div className="text-xs text-slate-500">+{formatCurrency(pkg.priceDifference)} t.o.v. basis</div>
        ) : (
          <div className="text-xs text-slate-500">Basisprijs</div>
        )}
      </div>
      <ul className="flex-1 space-y-1 text-xs text-slate-700">
        {pkg.features.map((f, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <svg className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onChoose}
        className={clsx(
          'w-full px-3 py-2 text-sm font-medium rounded-md transition-colors',
          isCurrent
            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        )}
      >
        Kies dit pakket
      </button>
    </div>
  );
}

const EMPTY_SERVICE_ADDRESS: OfferteServiceAddress = {
  pickup: { street: '', postalCode: '', city: '', toevoeging: '' },
  delivery: { street: '', postalCode: '', city: '', toevoeging: '' },
  useAlternateDelivery: false,
};

export function OfferteDetailView({ offerteId, variant, onClose }: OfferteDetailViewProps) {
  const navigate = useNavigate();
  const [offerte, setOfferte] = useState<OfferteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [sendPdfOpen, setSendPdfOpen] = useState(false);
  const [sentRefreshKey, setSentRefreshKey] = useState(0);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfPackageSku, setPdfPackageSku] = useState<string>('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);

  const [behandelingDialog, setBehandelingDialog] = useState<{
    parentId: string;
    parentName: string;
  } | null>(null);
  const [removingBehandelingId, setRemovingBehandelingId] = useState<string | null>(null);
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [serviceAddressOpen, setServiceAddressOpen] = useState(false);

  const [editKleed, setEditKleed] = useState<OfferteProduct | null>(null);
  const [addKleedOpen, setAddKleedOpen] = useState(false);
  const [addFeeOpen, setAddFeeOpen] = useState(false);
  const [removeKleedConfirm, setRemoveKleedConfirm] = useState<OfferteProduct | null>(null);
  const [removingKleedId, setRemovingKleedId] = useState<string | null>(null);
  const [removingToeslagId, setRemovingToeslagId] = useState<string | null>(null);
  const [mutationBanner, setMutationBanner] = useState<string | null>(null);

  const openPanel = useOffertePanelStore((s) => s.open);
  const submitResult = useOffertePanelStore((s) => s.submitResult);
  const closePanel = useOffertePanelStore((s) => s.close);

  const isDrawer = variant === 'drawer';

  const refetch = useCallback(() => {
    fetchOfferteDetail(offerteId)
      .then(setOfferte)
      .catch(() => {/* stil */});
  }, [offerteId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorCode(null);
    setNotFound(false);
    fetchOfferteDetail(offerteId)
      .then((data) => {
        if (cancelled) return;
        setOfferte(data);
        setPdfPackageSku(data.currentPackageSku || '');
        setLoading(false);
      })
      .catch((err: Error & { status?: number; code?: string; workOrderId?: string }) => {
        if (cancelled) return;
        if (err.code === 'ALREADY_CONVERTED' && err.workOrderId) {
          // Lead is al omgezet naar een order — direct doorsturen in plaats
          // van een dead-end foutmelding (vervolgorder, back-button, oude link).
          if (isDrawer && onClose) onClose();
          navigate(`/orders/${err.workOrderId}`, { replace: true });
          return;
        }
        if (err.status === 404) {
          setNotFound(true);
        } else {
          setError(err.message || 'Kon offerte niet laden');
          setErrorCode(err.code || null);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [offerteId]);

  // Na succesvolle acceptatie: door naar de aangemaakte Werk Order.
  // Fallback: refetch (pagina toont dan de geconverteerde lead-status).
  useEffect(() => {
    if (!submitResult?.ok) return;
    if (submitResult.workOrderId) {
      if (isDrawer && onClose) onClose();
      closePanel();
      navigate(`/orders/${submitResult.workOrderId}`);
      return;
    }
    refetch();
  }, [submitResult, refetch, navigate, closePanel, isDrawer, onClose]);

  const exitBack = () => {
    if (isDrawer && onClose) onClose();
    else navigate('/offertes');
  };

  const handlePdfDownload = useCallback(async () => {
    if (!offerte) return;
    setPdfDownloading(true);
    setActionMessage(null);
    try {
      await downloadOffertePdf(offerte.leadId, {
        packageSku: pdfPackageSku || undefined,
      });
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : 'PDF-download mislukt');
    } finally {
      setPdfDownloading(false);
    }
  }, [offerte, pdfPackageSku]);

  const handleStatusChange = useCallback(async (newStatus: string) => {
    if (!offerte || newStatus === offerte.status) return;
    if (
      newStatus === LEAD_CLOSED_STATUS &&
      !window.confirm("Offerte markeren als 'Niet geïnteresseerd'?")
    ) {
      return;
    }
    const prevStatus = offerte.status;
    setStatusBusy(true);
    setActionMessage(null);
    setOfferte((o) => (o ? { ...o, status: newStatus } : o)); // optimistic
    try {
      await setOfferteStatus(offerte.leadId, newStatus);
      refetch();
    } catch (err) {
      setOfferte((o) => (o ? { ...o, status: prevStatus } : o)); // rollback
      setActionMessage(err instanceof Error ? err.message : 'Status wijzigen mislukt');
    } finally {
      setStatusBusy(false);
    }
  }, [offerte, refetch]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Offerte laden...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3">
        <p className="text-sm">Offerte niet gevonden</p>
        <button onClick={exitBack} className="text-indigo-600 text-sm hover:underline">
          {isDrawer ? 'Sluiten' : 'Terug naar offertes'}
        </button>
      </div>
    );
  }

  if (error && !offerte) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 max-w-xl text-center">
          {error}
        </div>
        {errorCode === 'NO_SIGNATURE' && (
          <button
            onClick={() => setResendDialogOpen(true)}
            className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
          >
            Opnieuw versturen
          </button>
        )}
        <button onClick={exitBack} className="text-indigo-600 text-sm hover:underline">
          {isDrawer ? 'Sluiten' : 'Terug'}
        </button>
        <ConfirmDialog
          isOpen={resendDialogOpen}
          title="Offerte opnieuw versturen"
          body="De klant ontvangt een nieuwe offerte-email met een link om de offerte online te bevestigen."
          confirmLabel="Versturen"
          isBusy={actionBusy}
          onCancel={() => setResendDialogOpen(false)}
          onConfirm={async () => {
            setActionBusy(true);
            try {
              await resendOfferte(offerteId);
              setResendDialogOpen(false);
              setActionMessage('Offerte verzonden');
              refetch();
            } catch (err) {
              setActionMessage(err instanceof Error ? err.message : 'Versturen mislukt');
            } finally {
              setActionBusy(false);
            }
          }}
        />
        {actionMessage && <p className="text-xs text-slate-500">{actionMessage}</p>}
      </div>
    );
  }

  if (!offerte) return null;

  const minimumWarning =
    offerte.totals.minimumOrderInclBtw != null &&
    offerte.totals.eindTotaal < offerte.totals.minimumOrderInclBtw;

  // Korting kan alleen op een nog-niet-geaccepteerde, niet-gesloten offerte.
  const mutationsAllowed =
    !offerte.isAlreadyAccepted && offerte.status !== LEAD_CLOSED_STATUS;
  // Telefonisch doorzetten namens de klant: zelfde panel als de pakketkaarten,
  // maar bovenin bereikbaar met het huidige (of basis-)pakket voorgeselecteerd.
  const canAccept = mutationsAllowed && offerte.packages.length > 0;
  const acceptSku = (
    offerte.packages.some((p) => p.sku === offerte.currentPackageSku)
      ? offerte.currentPackageSku
      : (offerte.packages[0]?.sku ?? 'WAS-PACK-VLOERKLEED-BASIS')
  ) as PackageSku;
  const openAcceptPanel = () => openPanel(offerte, acceptSku, refetch);
  const discount: OfferteDiscount =
    offerte.discount ?? { kind: 'none', percentage: null, code: null, totalAmountInclBtw: 0 };

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Drawer sticky header */}
      {isDrawer && (
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            aria-label="Sluiten"
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-sm font-semibold text-slate-900 truncate">{offerte.leadnummer || 'Offerte'}</h2>
          {offerte.status && (
            <OfferteStatusSelect
              value={offerte.status}
              busy={statusBusy}
              onChange={handleStatusChange}
              className="flex-shrink-0"
            />
          )}
          {offerte.status && <TerugkomMomentPill leadId={offerte.leadId} status={offerte.status} className="flex-shrink-0" />}
          <Link
            to={`/offertes/${offerte.leadId}`}
            onClick={onClose}
            className="ml-auto text-xs text-indigo-600 hover:underline flex-shrink-0"
          >
            Open volledig →
          </Link>
        </div>
      )}

      {/* Page header */}
      {!isDrawer && (
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <button
            onClick={() => navigate('/offertes')}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Offertes
          </button>

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="text-xl font-semibold text-slate-900">
                  {offerte.leadnummer || 'Offerte'}
                </h1>
                {offerte.status && (
                  <OfferteStatusSelect
                    value={offerte.status}
                    busy={statusBusy}
                    onChange={handleStatusChange}
                  />
                )}
                {offerte.status && <TerugkomMomentPill leadId={offerte.leadId} status={offerte.status} />}
                {offerte.brand && (
                  <span
                    className={clsx(
                      'text-xs px-2 py-0.5 rounded font-medium',
                      BRAND_COLORS[offerte.brand] || 'bg-slate-100 text-slate-600'
                    )}
                  >
                    {offerte.brand}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {offerte.offerPublicUrl && (
                  <a
                    href={offerte.offerPublicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open de offertelink zoals de klant die ontvangt (offerte.kleda.nl)"
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    <ExternalLinkIcon />
                    Klantweergave
                  </a>
                )}
                {canAccept && (
                  <button
                    onClick={openAcceptPanel}
                    title="Offerte namens de klant accepteren en direct omzetten naar een order"
                    className="text-xs px-2.5 py-1 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700"
                  >
                    Doorzetten naar order
                  </button>
                )}
                <button
                  onClick={() => setResendDialogOpen(true)}
                  className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  Opnieuw versturen
                </button>
                <button
                  onClick={() => setCloseDialogOpen(true)}
                  className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  Niet geïnteresseerd
                </button>
                {offerte.packages.length > 0 && (
                  <select
                    value={pdfPackageSku}
                    onChange={(e) => setPdfPackageSku(e.target.value)}
                    title="Pakket dat in de PDF uitgelicht wordt"
                    className="text-xs px-2 py-1 rounded-md border border-slate-300 bg-white text-slate-700"
                  >
                    {offerte.packages.map((pkg) => (
                      <option key={pkg.sku} value={pkg.sku}>
                        PDF: {pkg.label}
                        {pkg.priceDifference > 0 ? ` (+${formatCurrency(pkg.priceDifference)})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={handlePdfDownload}
                  disabled={pdfDownloading}
                  title="Download offerte als PDF"
                  className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {pdfDownloading ? 'PDF...' : 'PDF downloaden'}
                </button>
                <button
                  onClick={() => setSendPdfOpen(true)}
                  title="Stuur PDF-offerte per e-mail"
                  className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  PDF mailen
                </button>
                <button
                  onClick={() => setComposerOpen(true)}
                  disabled={!offerte.customer.email}
                  title={!offerte.customer.email ? 'Geen e-mailadres bekend' : 'Stuur e-mail'}
                  className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  Stuur e-mail
                </button>
                {actionMessage && (
                  <span className="text-xs text-slate-500">{actionMessage}</span>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-2xl font-semibold text-slate-900">
                {formatCurrency(offerte.totals.eindTotaal)}
              </div>
              <div className="text-xs text-slate-500">Incl. BTW</div>
              <div className="mt-1.5 flex flex-wrap justify-end gap-1.5">
                <BundelkortingToggle
                  recordId={offerte.leadId}
                  geenBundelkorting={offerte.geenBundelkorting === true}
                  canEdit={mutationsAllowed}
                  onSaved={refetch}
                />
                <OfferteKortingPill
                  leadId={offerte.leadId}
                  discount={discount}
                  canEdit={mutationsAllowed}
                  onSaved={refetch}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drawer compact action bar */}
      {isDrawer && (
        <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap flex-shrink-0">
          {offerte.brand && (
            <span
              className={clsx(
                'text-xs px-2 py-0.5 rounded font-medium',
                BRAND_COLORS[offerte.brand] || 'bg-slate-100 text-slate-600'
              )}
            >
              {offerte.brand}
            </span>
          )}
          {offerte.offerPublicUrl && (
            <a
              href={offerte.offerPublicUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open de offertelink zoals de klant die ontvangt (offerte.kleda.nl)"
              className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <ExternalLinkIcon />
              Klantweergave
            </a>
          )}
          {canAccept && (
            <button
              onClick={openAcceptPanel}
              title="Offerte namens de klant accepteren en direct omzetten naar een order"
              className="text-xs px-2.5 py-1 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700"
            >
              Doorzetten naar order
            </button>
          )}
          <button
            onClick={() => setResendDialogOpen(true)}
            className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Opnieuw versturen
          </button>
          <button
            onClick={() => setCloseDialogOpen(true)}
            className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Niet geïnteresseerd
          </button>
          {offerte.packages.length > 0 && (
            <select
              value={pdfPackageSku}
              onChange={(e) => setPdfPackageSku(e.target.value)}
              title="Pakket dat in de PDF uitgelicht wordt"
              className="text-xs px-2 py-1 rounded-md border border-slate-300 bg-white text-slate-700"
            >
              {offerte.packages.map((pkg) => (
                <option key={pkg.sku} value={pkg.sku}>
                  {pkg.label}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={handlePdfDownload}
            disabled={pdfDownloading}
            title="Download offerte als PDF"
            className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {pdfDownloading ? 'PDF...' : 'PDF'}
          </button>
          <button
            onClick={() => setSendPdfOpen(true)}
            title="Stuur PDF-offerte per e-mail"
            className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            PDF mailen
          </button>
          <button
            onClick={() => setComposerOpen(true)}
            disabled={!offerte.customer.email}
            title={!offerte.customer.email ? 'Geen e-mailadres bekend' : 'Stuur e-mail'}
            className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            Stuur e-mail
          </button>
          <div className="ml-auto text-right">
            <div className="text-base font-semibold text-slate-900 leading-tight">
              {formatCurrency(offerte.totals.eindTotaal)}
            </div>
            <div className="text-[11px] text-slate-500 leading-tight">Incl. BTW</div>
            <div className="mt-1 flex flex-wrap justify-end gap-1.5">
              <BundelkortingToggle
                recordId={offerte.leadId}
                geenBundelkorting={offerte.geenBundelkorting === true}
                canEdit={mutationsAllowed}
                onSaved={refetch}
              />
              <OfferteKortingPill
                leadId={offerte.leadId}
                discount={discount}
                canEdit={mutationsAllowed}
                onSaved={refetch}
              />
            </div>
          </div>
          {actionMessage && (
            <span className="text-xs text-slate-500 w-full">{actionMessage}</span>
          )}
        </div>
      )}

      {/* Body */}
      <div className={clsx('flex-1 overflow-y-auto space-y-6', isDrawer ? 'p-4' : 'p-6')}>
        {offerte.isAlreadyAccepted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-700">
            Deze offerte is al geaccepteerd.
          </div>
        )}

        {/* Grid: customer + status */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Klant
              </h2>
              <button
                type="button"
                onClick={() => setEditCustomerOpen(true)}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                title="Klantgegevens bewerken"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
                Bewerken
              </button>
            </div>
            <Link
              to={`/klanten/${offerte.leadId}?type=lead`}
              className="text-sm font-medium text-indigo-600 hover:underline block"
            >
              {offerte.customer.name || '(geen naam)'}
            </Link>
            {offerte.customer.company && (
              <p className="text-xs text-slate-700 mt-0.5">{offerte.customer.company}</p>
            )}
            <div className="text-xs text-slate-500 mt-2 space-y-1">
              {offerte.customer.email && (
                <a href={`mailto:${offerte.customer.email}`} className="block hover:text-indigo-600 truncate">
                  {offerte.customer.email}
                </a>
              )}
              {offerte.customer.phone && (
                <div>
                  <CallButton
                    number={offerte.customer.phone}
                    brand={(offerte.brand as Brand) || undefined}
                    customerName={offerte.customer.name ?? undefined}
                    sourceContext="offerte-detail"
                    variant="inline"
                  >
                    {offerte.customer.phone}
                  </CallButton>
                </div>
              )}
              {(offerte.customer.street || offerte.customer.city) && (
                <p className="text-slate-600">
                  {[
                    `${offerte.customer.street} ${offerte.customer.houseNumber}`.trim(),
                    `${offerte.customer.postalCode} ${offerte.customer.city}`.trim(),
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>

            {/* Ophaal-/afleveradres — stuurt het route-gebied dat de klant ziet */}
            {(() => {
              const sa = offerte.serviceAddress ?? EMPTY_SERVICE_ADDRESS;
              const pickupSet = !!(sa.pickup.postalCode || sa.pickup.street);
              const deliverySet =
                sa.useAlternateDelivery && !!(sa.delivery.postalCode || sa.delivery.street);
              const fmt = (a: { street: string; postalCode: string; city: string }) =>
                [a.street, `${a.postalCode} ${a.city}`.trim()].filter(Boolean).join(', ');
              return (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs space-y-0.5 min-w-0">
                      <p className="text-slate-500">Ophaaladres</p>
                      {pickupSet ? (
                        <p className="text-slate-700 truncate">{fmt(sa.pickup)}</p>
                      ) : (
                        <p className="text-slate-400">Zelfde als klantadres</p>
                      )}
                      {deliverySet && (
                        <>
                          <p className="text-slate-500 mt-1">Afleveradres</p>
                          <p className="text-slate-700 truncate">{fmt(sa.delivery)}</p>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setServiceAddressOpen(true)}
                      className="text-xs text-indigo-600 hover:text-indigo-700 whitespace-nowrap"
                    >
                      {pickupSet || deliverySet ? 'Wijzigen' : '+ Toevoegen'}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Status
            </h2>
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-500">Status</span>
                <span className="text-slate-900">{offerte.status || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Aangemaakt</span>
                <span className="text-slate-900">{formatDate(offerte.createdDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Laatst gewijzigd</span>
                <span className="text-slate-900">{formatDateTime(offerte.lastModifiedDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Postcodegebied</span>
                <span className="text-slate-900">{offerte.customer.postcodegebied || '—'}</span>
              </div>
            </div>
          </div>
        </div>

        {mutationBanner && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-3">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <span className="flex-1">{mutationBanner}</span>
            <button
              type="button"
              onClick={() => setMutationBanner(null)}
              className="text-amber-700 hover:text-amber-900 flex-shrink-0"
              aria-label="Sluiten"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Kleden */}
        {(() => {
          const grouped = groupKledenMetBehandelingen(offerte.products);
          async function handleRemoveBehandeling(childId: string) {
            setRemovingBehandelingId(childId);
            setActionMessage(null);
            try {
              const res = await removeKleedBehandeling(childId);
              if (!res.success) {
                setActionMessage(res.message || 'Verwijderen mislukt');
                return;
              }
              refetch();
            } catch (err) {
              setActionMessage(err instanceof Error ? err.message : 'Verwijderen mislukt');
            } finally {
              setRemovingBehandelingId(null);
            }
          }
          return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">
              Kleden ({grouped.length})
            </h2>
            {mutationsAllowed && (
              <button
                type="button"
                onClick={() => setAddKleedOpen(true)}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Kleed toevoegen
              </button>
            )}
          </div>
          {grouped.length === 0 ? (
            <p className="px-6 py-6 text-sm text-slate-400 text-center">Geen kleden</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {grouped.map(({ kleed, behandelingen }) => (
                <KleedCard
                  key={kleed.id}
                  kleed={kleed}
                  behandelingen={behandelingen}
                  onAddBehandeling={(parentId, parentName) =>
                    setBehandelingDialog({ parentId, parentName })
                  }
                  onRemoveBehandeling={handleRemoveBehandeling}
                  onEditKleed={(k) => setEditKleed(k)}
                  onRemoveKleed={(k) => setRemoveKleedConfirm(k)}
                  removingId={removingBehandelingId}
                  removingKleedId={removingKleedId}
                  mutationsAllowed={mutationsAllowed}
                />
              ))}
            </div>
          )}
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 rounded-b-xl">
            {(() => {
              // Totalen in incl. BTW — zelfde taal als de kaarten (bruto incl. per regel)
              // en als de PDF. Subtotaal = som van de getoonde regelprijzen, zodat het
              // exact op de kaarten aansluit; korting = subtotaal − eindtotaal (residueel),
              // zodat Subtotaal − Korting = Totaal altijd sluit. BTW staat al ín het totaal.
              const subtotaalInclBruto = grouped.reduce((sum, { kleed, behandelingen }) => {
                const k = kleed.totaalInclBtwBruto ?? kleed.totaalInclBtw ?? 0;
                const b = behandelingen.reduce(
                  (s, x) => s + (x.totaalInclBtwBruto ?? x.totaalInclBtw ?? 0),
                  0,
                );
                return sum + k + b;
              }, 0);
              const eindTotaal = offerte.totals.eindTotaal;
              const kortingIncl = subtotaalInclBruto - eindTotaal;
              const heeftKorting = (offerte.totals.kortingBedrag ?? 0) > 0 && kortingIncl > 0.005;
              const kortingPct =
                offerte.totals.kortingPercentage != null && offerte.totals.kortingPercentage > 0
                  ? offerte.totals.kortingPercentage
                  : null;
              const totaalLabel =
                offerte.packages.length > 0 ? 'Basispakket incl. BTW' : 'Totaal incl. BTW';
              return (
                <>
                  {heeftKorting && (
                    <>
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>Subtotaal</span>
                        <span>{formatCurrency(subtotaalInclBruto)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-emerald-600 mt-0.5">
                        <span>{kortingPct ? `Korting (${kortingPct}%)` : 'Korting'}</span>
                        <span>-{formatCurrency(kortingIncl)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-sm font-medium text-slate-900 mt-1">
                    <span>{totaalLabel}</span>
                    <span>{formatCurrency(eindTotaal)}</span>
                  </div>
                  <div className="flex justify-between text-[11px] text-slate-400 mt-0.5">
                    <span>Waarvan 21% BTW</span>
                    <span>{formatCurrency(offerte.totals.btwTotaal)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
          );
        })()}

        {/* Toeslagen / kosten (Bijrijder, transport, …) */}
        {(() => {
          const toeslagen = offerte.products.filter(isToeslag);
          if (!mutationsAllowed && toeslagen.length === 0) return null;
          async function handleRemoveToeslag(id: string) {
            setRemovingToeslagId(id);
            setActionMessage(null);
            try {
              const res = await removeLeadProduct({ leadProductId: id });
              if (!res.success) {
                setActionMessage(res.message || 'Verwijderen mislukt');
                return;
              }
              refetch();
            } catch (err) {
              setActionMessage(err instanceof Error ? err.message : 'Verwijderen mislukt');
            } finally {
              setRemovingToeslagId(null);
            }
          }
          return (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-900">
                  Toeslagen / kosten ({toeslagen.length})
                </h2>
                {mutationsAllowed && (
                  <button
                    type="button"
                    onClick={() => setAddFeeOpen(true)}
                    className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Toeslag / kosten toevoegen
                  </button>
                )}
              </div>
              {toeslagen.length === 0 ? (
                <p className="px-6 py-5 text-sm text-slate-400 text-center">Geen toeslagen</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {toeslagen.map((t) => {
                    const qty = t.quantity ?? 1;
                    const prijs = t.totaalInclBtwBruto ?? t.totaalInclBtw ?? 0;
                    return (
                      <div key={t.id} className="px-6 py-3 flex items-center justify-between gap-3">
                        <span className="text-sm text-slate-900 truncate">
                          {t.productName || t.productCode}
                          {qty > 1 ? ` × ${qty}` : ''}
                        </span>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-sm text-slate-700">{formatCurrency(prijs)}</span>
                          {mutationsAllowed && (
                            <button
                              type="button"
                              disabled={removingToeslagId === t.id}
                              onClick={() => handleRemoveToeslag(t.id)}
                              className="text-slate-300 hover:text-red-500 disabled:opacity-50"
                              aria-label="Verwijder toeslag"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {minimumWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
            Het totaal ligt onder het minimum van{' '}
            {formatCurrency(offerte.totals.minimumOrderInclBtw ?? 0)}. De klant moet bijbetalen
            voor de minimum-ordertoeslag.
          </div>
        )}

        {/* Pakketten — alleen voor brands die packages ondersteunen (Kleda) */}
        {offerte.packages.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Pakketten</h2>
            <div
              className={clsx(
                'grid gap-3 grid-cols-1',
                isDrawer ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-4'
              )}
            >
              {offerte.packages.map((pkg) => (
                <PackageCard
                  key={pkg.sku}
                  pkg={pkg}
                  isCurrent={pkg.sku === offerte.currentPackageSku}
                  onChoose={() => openPanel(offerte, pkg.sku as PackageSku, refetch)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Verzonden offerte-mails — audit + delivery status van Resend */}
        <VerzondenOffertesSection
          leadId={offerte.leadId}
          refreshKey={sentRefreshKey}
          variant={isDrawer ? 'drawer' : 'page'}
        />

        {/* System info */}
        <div className="text-xs text-slate-400 flex items-center gap-4 flex-wrap">
          <span>Leadnr: {offerte.leadnummer}</span>
          <span>Aangemaakt: {formatDate(offerte.createdDate)}</span>
        </div>
      </div>

      <ConfirmDialog
        isOpen={resendDialogOpen}
        title="Offerte opnieuw versturen"
        body="De klant ontvangt een nieuwe offerte-email met een link om de offerte online te bevestigen."
        confirmLabel="Versturen"
        isBusy={actionBusy}
        onCancel={() => setResendDialogOpen(false)}
        onConfirm={async () => {
          setActionBusy(true);
          setActionMessage(null);
          try {
            await resendOfferte(offerte.leadId);
            setResendDialogOpen(false);
            setActionMessage('Offerte verzonden');
            refetch();
          } catch (err) {
            setActionMessage(err instanceof Error ? err.message : 'Versturen mislukt');
          } finally {
            setActionBusy(false);
          }
        }}
      />

      <ConfirmDialog
        isOpen={closeDialogOpen}
        title="Offerte sluiten"
        body="Weet je zeker dat je deze offerte wil markeren als 'Niet geïnteresseerd'?"
        confirmLabel="Ja, sluiten"
        confirmVariant="danger"
        isBusy={actionBusy}
        onCancel={() => setCloseDialogOpen(false)}
        onConfirm={async () => {
          setActionBusy(true);
          setActionMessage(null);
          try {
            await setOfferteStatus(offerte.leadId, LEAD_CLOSED_STATUS);
            setCloseDialogOpen(false);
            if (isDrawer && onClose) onClose();
            else navigate('/offertes');
          } catch (err) {
            setActionMessage(err instanceof Error ? err.message : 'Sluiten mislukt');
          } finally {
            setActionBusy(false);
          }
        }}
      />

      <OutboundComposerModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        customer={{
          id: offerte.leadId,
          name: offerte.customer.name || '',
          email: offerte.customer.email || '',
          phone: offerte.customer.phone || '',
        }}
        orders={offerte.brand ? [{ brand: offerte.brand }] : undefined}
        prefillAanleiding="Offerte-opvolging"
      />

      <SendPdfDialog
        isOpen={sendPdfOpen}
        leadId={offerte.leadId}
        leadnummer={offerte.leadnummer}
        brand={offerte.brand}
        customerName={offerte.customer.name || ''}
        customerEmail={offerte.customer.email || ''}
        packages={offerte.packages}
        defaultPackageSku={pdfPackageSku || offerte.currentPackageSku || ''}
        onClose={() => setSendPdfOpen(false)}
        onSent={({ contentDocumentId }) => {
          setActionMessage(
            contentDocumentId
              ? 'PDF verzonden en gearchiveerd in Salesforce'
              : 'PDF verzonden (archivering mislukt — controleer Salesforce)',
          );
          setSentRefreshKey((k) => k + 1);
        }}
      />

      <KleedBehandelingDialog
        isOpen={behandelingDialog !== null}
        parentLeadProductId={behandelingDialog?.parentId ?? null}
        parentName={behandelingDialog?.parentName}
        onClose={() => setBehandelingDialog(null)}
        onAdded={() => {
          refetch();
        }}
      />

      <EditCustomerDialog
        isOpen={editCustomerOpen}
        leadId={offerte.leadId}
        customer={offerte.customer}
        onClose={() => setEditCustomerOpen(false)}
        onSaved={refetch}
      />

      <ServiceAddressDialog
        isOpen={serviceAddressOpen}
        leadId={offerte.leadId}
        initial={offerte.serviceAddress ?? EMPTY_SERVICE_ADDRESS}
        customerPostalCode={offerte.customer.postalCode}
        onClose={() => setServiceAddressOpen(false)}
        onSaved={() => {
          refetch();
          setMutationBanner(
            'Ophaal-/afleveradres opgeslagen. Klik "Opnieuw versturen" om de klant de bijgewerkte offerte (met de juiste ophaaldagen) te sturen.'
          );
        }}
      />

      <EditKleedDialog
        isOpen={editKleed !== null}
        leadProduct={editKleed}
        onClose={() => setEditKleed(null)}
        onSaved={() => {
          refetch();
          setMutationBanner(
            'Wijzigingen opgeslagen. Klik "Opnieuw versturen" om de klant de bijgewerkte offerte te sturen.'
          );
        }}
      />

      <AddKleedDialog
        isOpen={addKleedOpen}
        leadId={offerte.leadId}
        onClose={() => setAddKleedOpen(false)}
        onAdded={() => {
          refetch();
          setMutationBanner(
            'Kleed toegevoegd. Klik "Opnieuw versturen" om de klant de bijgewerkte offerte te sturen.'
          );
        }}
      />

      <AddOfferteFeeDialog
        isOpen={addFeeOpen}
        leadId={offerte.leadId}
        onClose={() => setAddFeeOpen(false)}
        onAdded={() => {
          refetch();
          setMutationBanner(
            'Toeslag toegevoegd. Klik "Opnieuw versturen" om de klant de bijgewerkte offerte te sturen.'
          );
        }}
      />

      <ConfirmDialog
        isOpen={removeKleedConfirm !== null}
        title="Kleed verwijderen"
        body={(() => {
          if (!removeKleedConfirm) return '';
          const grouped = groupKledenMetBehandelingen(offerte.products).find(
            (g) => g.kleed.id === removeKleedConfirm.id
          );
          const childCount = grouped?.behandelingen.length ?? 0;
          if (childCount > 0) {
            return `Dit kleed verwijdert ook ${childCount} gekoppelde behandeling${childCount === 1 ? '' : 'en'}.`;
          }
          return 'Het kleed (en de bijbehorende pakket-koppeling) wordt verwijderd.';
        })()}
        confirmLabel="Verwijderen"
        confirmVariant="danger"
        isBusy={removingKleedId !== null}
        onCancel={() => setRemoveKleedConfirm(null)}
        onConfirm={async () => {
          const target = removeKleedConfirm;
          if (!target) return;
          setRemovingKleedId(target.id);
          try {
            const res = await removeLeadProduct({ leadProductId: target.id });
            if (!res.success) {
              setActionMessage(res.message || 'Verwijderen mislukt');
              return;
            }
            setRemoveKleedConfirm(null);
            refetch();
            setMutationBanner(
              'Kleed verwijderd. Klik "Opnieuw versturen" om de klant de bijgewerkte offerte te sturen.'
            );
          } catch (err) {
            setActionMessage(err instanceof Error ? err.message : 'Verwijderen mislukt');
          } finally {
            setRemovingKleedId(null);
          }
        }}
      />
    </div>
  );
}
